import {
  CircularProgress,
  FormControl,
  FormHelperText,
  makeStyles,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  colors,
  useMediaQuery
} from '@material-ui/core'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import React from 'react'

import { fetchUser } from 'api'
import Button from 'app/components/generic/Button'
import { Select, TextField } from 'app/components/generic/FormElements'
import constants from 'app/constants'
import { confirm } from 'app/helpers'
import {
  validateDate,
  validateNotEmpty,
  validateNotNilAndBlank,
  validatePhoneNumber
} from 'app/validators'

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: '0.75vmin 0 0.75vmin 0'
  },
  select: {
    margin: '0.75vmin 0 0.75vmin 0'
  },
  formError: {
    marginTop: 0,
    marginBottom: 0
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.white,
    margin: '1.25vmin 0 0 0',
    fontWeight: '800',
    textTransform: 'lowercase',
    borderRadius: '50px',
    '&:hover': {
      backgroundColor: colors.grey[200],
      color: '#000',
      boxShadow: 'none'
    }
  }
}))

const VisitorEntryForm = (props) => {
  const {
    formData,
    purposeOptions,
    attributionOptions,
    loadingSubmit,
    handleOnSubmitClick,
    userExistsFunction
  } = props
  const classes = useStyles()
  const matches = useMediaQuery('(min-width:1441px)')
  const { errors, values, setFieldValue, dirty, isValid } = useFormikContext()

  const handleParentNumberChange = async (value) => {
    setFieldValue('parentNumber', value?.target?.value)

    const phoneNumberValidatorRegex = /^[0-9]{10}$/
    if (phoneNumberValidatorRegex.test(value?.target?.value)) {
      const response = await fetchUser(
        {
          queries: {
            phone_number: value?.target?.value
          }
        },
        constants.PARENT
      )

      if (response?.count >= 1) {
        confirm(userExistsFunction, 'This phone number already exists!', null, {
          title: '',
          showCancelButton: false,
          allowOutsideClick: false,
          confirmButtonText: 'Okay'
        })
      }
    }
  }

  const form = React.useMemo(() => {
    return (
      <>
        <FormControl
          className={classes.select}
          fullWidth
          variant="outlined"
          {...(!isEmpty(errors.parentNumber) ? { error: true } : null)}
          required
        >
          <TextField
            name={'parentNumber'}
            label={'Phone Number'}
            variant="outlined"
            size={matches ? 'normal' : 'small'}
            autoComplete="off"
            required
            onChange={handleParentNumberChange}
            validate={validatePhoneNumber({ isRequired: true })}
          />
        </FormControl>
        <FormControl
          className={classes.select}
          fullWidth
          variant="outlined"
          {...(!isEmpty(errors.parentName) ? { error: true } : null)}
          required
        >
          <TextField
            name={'parentName'}
            label={'Parent Full Name'}
            variant="outlined"
            autoComplete="off"
            size={matches ? 'normal' : 'small'}
            required
            validate={validateNotNilAndBlank({ isRequired: true })}
          />
        </FormControl>
        <FormControl
          className={classes.select}
          fullWidth
          variant="outlined"
          {...(!isEmpty(errors.childName) ? { error: true } : null)}
          required
        >
          <TextField
            name={'childName'}
            label={'Child Full Name'}
            variant="outlined"
            autoComplete="off"
            size={matches ? 'normal' : 'small'}
            required
            validate={validateNotNilAndBlank({ isRequired: true })}
          />
        </FormControl>

        <FormControl
          fullWidth
          variant="outlined"
          required
          className={classes.select}
          {...(errors.childDOB ? { error: true } : null)}
        >
          <TextField
            name={'childDOB'}
            label={"Child's Date of Birth"}
            type="date"
            defaultValue={formData['childDOB']}
            value={values['childDOB']}
            InputLabelProps={{
              shrink: true
            }}
            variant="outlined"
            size={matches ? 'normal' : 'small'}
            required
            validate={validateDate({ isRequired: true })}
          />
        </FormControl>

        <FormControl
          fullWidth
          variant="outlined"
          size={matches ? 'normal' : 'small'}
          required
          className={classes.select}
          {...(errors.purposeOfVisit ? { error: true } : null)}
        >
          <InputLabel id="purposeOfVisit-session-select">
            {`Purpose of the visit`}
          </InputLabel>
          <Select
            name="purposeOfVisit"
            label="Purpose of the visit"
            labelId="purposeOfVisit-session-select"
            value={values['purposeOfVisit']}
            multiple
            input={<OutlinedInput label="Purpose of the visit" />}
            renderValue={(selected) => selected.join(', ')}
            placeholder={'Purpose of the visit'}
            validate={validateNotEmpty({ isRequired: true })}
          >
            {purposeOptions?.map((item) => (
              <MenuItem key={item} value={item}>
                <Checkbox
                  checked={values['purposeOfVisit'].includes(item)}
                  color="primary"
                />
                <ListItemText primary={item} />
              </MenuItem>
            ))}
          </Select>
          {errors.purposeOfVisit && (
            <FormHelperText className={classes.formError}>
              {errors.purposeOfVisit}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          variant="outlined"
          size={matches ? 'normal' : 'small'}
          required
          className={classes.select}
          {...(errors.attribution ? { error: true } : null)}
        >
          <InputLabel id="attribution-select">
            How did you hear about us?
          </InputLabel>
          <Select
            name="attribution"
            label="How did you hear about us?"
            labelId="attribution"
            value={values['attribution']}
            placeholder={'How did you hear about us?'}
            validate={validateNotEmpty({ isRequired: true })}
          >
            {attributionOptions?.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
          {errors.attribution && (
            <FormHelperText className={classes.formError}>
              {errors.attribution}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl required={true} fullWidth className={classes.formControl}>
          <Button
            variant="contained"
            onClick={handleOnSubmitClick}
            disabled={
              loadingSubmit
              // ||
              // !(dirty && isValid) ||
              // isEmpty(values['childDOB'])
            }
            disableElevation
            className={classes.button}
          >
            {loadingSubmit ? <CircularProgress color="primary" /> : 'submit'}
          </Button>
        </FormControl>
      </>
    )
  }, [errors, loadingSubmit, attributionOptions, purposeOptions, values])

  return <>{form}</>
}

export default VisitorEntryForm
